import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import CommunityWall from '../../components/common/CommunityWall';
import QuizStats from '../../components/common/QuizStats';
import MailSignOff from '../../components/common/MailSignOff';
import MailCatalogue from '../../components/common/MailCatalogue';
import ExternalSurvey from '../../components/common/ExternalSurvey';
import CommsStats from '../../components/common/CommsStats';
import EventStats from '../../components/common/EventStats';
import CheerStats from '../../components/common/CheerStats';
import { FAMILY_ICONS } from '../../components/common/engagementSignals';
import { apiRequest, shortDate } from '../../utils';
import './MemberHome.css';
import './CompanyEvents.css';
import './CompanyEngagement.css';
import { LockedCreateButton, useCanBuild } from '../../components/common/ModuleInterest';

/* Who is actually taking part — the roster with a number beside every name.

   ★ The admin sees this. The employee does not: a personal score people can see is a culture
   decision rather than a feature toggle, and this is the version that answers "who have we lost"
   without turning the year into a competition.

   ★ Everybody on the roster is here, including the people at zero. A list of only the engaged
   cannot show you who is missing, which is the question the screen exists to answer.

   ★ The numbers come from the NIGHTLY pass, so the screen says when they were computed. Tuning a
   weight rescores this company immediately — new weights beside yesterday's numbers is the one
   thing it must never show.

   ★ **What counts lives on `/engagement/rules` since 2026-09-09**, behind the settings button
   beside the recompute. It was the second card here, which put a twenty-row settings form on the
   screen somebody opens to read a number. Its vocabulary — the family icons and a rule's label —
   moved to `components/common/engagementSignals` so this screen's per-person breakdown and that
   editor cannot drift.

   ★ Each person's tags show beside their name. Every other signal is something the person DID; a
   tag is something we decided about them, and a number that moved because of a label should be
   readable as such rather than hiding among the evenings they turned up to.

   ★ Tabs, because this screen is where a customer answers "how are we doing with our people" —
   and how we speak to them on their behalf is part of that question, not a separate errand.
   Communication settings landed here rather than behind a new home tile: the tile grid was
   already fifteen wide, and this screen was three cards thin. */

/* Only the tabs that have something behind them. A tab that renders nothing is worse than no
   tab — it reads as broken rather than as unbuilt.

   The order is the order the questions come in: who is taking part, who they ARE, what we did
   together, what they say about each other, and whether what we send them lands.

   ★ `community` carries no count. `people.length` is the roster, and the wall is the roster minus
   everybody who turned their profile off — a number on the tab that the screen behind it
   contradicts is worse than no number.

   ★ **How our mail signs off and what we send moved behind the gear (2026-09-09).** They are the
   two things on this screen you decide ONCE — after that they are furniture, and they were sitting
   in the middle of the row somebody opens to read a number. They stay tabs: the state is still
   `signoff` / `messages`, so `?tab=` links and the catalogue's "how do we sign" jump keep working.
   They are only no longer in the row that asks questions. */
const TABS = ['people', 'community', 'events', 'cheers', 'reach'];

/* Behind the gear — settings, not questions. `survey` is where a customer brings in the
   engagement survey they already run: decided once, then furniture, which is exactly what
   the gear is for. */
const SETTINGS_TABS = ['signoff', 'messages', 'survey'];

const ALL_TABS = [...TABS, ...SETTINGS_TABS];

/* Three bands, the same idea as the CRM score badge: taking part · showing up · not yet. */
const band = (score) => (score >= 40 ? 'is-high' : score >= 10 ? 'is-mid' : 'is-low');

/* The face in front of the name, the same one the roster and the guest list draw — an admin
   reading down this list is looking for a person, and a photograph is recognised before a name
   is read. Two letters when there is no photo: half this roster is colleagues who never claimed
   their account, so the fallback is the normal case rather than a broken image, and one initial
   does not tell two Jans apart. */
const initials = (text) => (text || '?')
  .trim().split(/\s+/).slice(0, 2).map((w) => w.charAt(0))
  .join('') || '?';

const Avatar = ({ person }) => (person.photo_url ? (
  <img className="ce-person__avatar" src={person.photo_url} alt="" />
) : (
  <span className="ce-person__avatar ce-person__avatar--empty">
    {initials(person.name || person.email)}
  </span>
));

const CompanyEngagement = () => {
  const { t, i18n } = useTranslation();
  // The paid "Own events" module behind every build button here (null while it loads).
  const canBuild = useCanBuild();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [openPerson, setOpenPerson] = useState(null);
  // ?tab= deep-links, which is how the guest-list screen's "how do we sign our mail" link and
  // any future mail-related pointer can land straight on the setting.
  const [tab, setTab] = useState(() => {
    const wanted = new URLSearchParams(window.location.search).get('tab');
    return ALL_TABS.includes(wanted) ? wanted : 'people';
  });

  const load = useCallback(() => {
    apiRequest('GET', '/company/engagement')
      .then(setData)
      .catch(() => navigate('/member-home'));
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const act = (promise) => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    promise
      .then(setData)
      .catch((err) => setNotice(t(err.message, { defaultValue: t('saveFailed') })))
      .finally(() => setBusy(false));
  };

  const people = (data && data.people) || [];
  const person = openPerson ? people.find((p) => p.id === openPerson) : null;
  const isSettings = SETTINGS_TABS.includes(tab);

  if (data === null) {
    return (
      <Base activeNav="home">
        <Card><span className="auth__spinner" /></Card>
      </Base>
    );
  }

  return (
    <Base activeNav="home">
      <Card>
        <Back to="/member-home" />
        <Title my="1" className="mt-3">
          <Icon name="chart-histogram" theme="primary" className="me-2" />
          {t('engagementTitle')}
        </Title>
        <p className="bravos-q__hint mb-2">
          {tab === 'people' ? t('engagementNote') : t(`engTabNote_${tab}`)}
        </p>

        <div className="ce-tabs">
          {TABS.map((name) => (
            <button
              key={name}
              type="button"
              className={`ce-tab ${tab === name ? 'is-on' : ''}`}
              onClick={() => setTab(name)}
            >
              {t(`engTab_${name}`)}
              {name === 'people' && <span className="ce-tab__n">{people.length}</span>}
            </button>
          ))}
          {/* Wordless on purpose: a sixth worded chip beside five questions is read as a sixth
              question, which is exactly what these two stopped being. */}
          <button
            type="button"
            className={`ce-tab ce-tab--gear ${isSettings ? 'is-on' : ''}`}
            onClick={() => { if (!isSettings) setTab(SETTINGS_TABS[0]); }}
            title={t('engSettings')}
            aria-label={t('engSettings')}
          >
            <Icon name="settings" />
          </button>
        </div>

        {/* The two settings, and only once you are in them — otherwise this is two more chips on
            a screen whose whole point was to carry fewer. */}
        {isSettings && (
          <div className="ce-tabs ce-tabs--sub">
            {SETTINGS_TABS.map((name) => (
              <button
                key={name}
                type="button"
                className={`ce-tab ${tab === name ? 'is-on' : ''}`}
                onClick={() => setTab(name)}
              >
                {t(`engTab_${name}`)}
              </button>
            ))}
          </div>
        )}

        {/* The headline number and the recompute belong to the score, so they follow it rather
            than sitting over a screen about how mail is signed. */}
        {tab === 'people' && (
          <>
            <div className="ce-row__top">
              <span className="ce-pill ce-pill--going">
                {t('engagedOfRoster', { n: data.scored, total: people.length })}
              </span>
              {data.computed_at && (
                <span className="ce-pill ce-pill--draft">
                  {t('computedOn', { date: shortDate(data.computed_at, i18n.language) })}
                </span>
              )}
            </div>
            <div className="bravos-form__row">
              {canBuild !== false && (
                <button
                  type="button"
                  className="bravos-submit bravos-submit--ghost"
                  disabled={busy}
                  onClick={() => act(apiRequest('POST', '/company/engagement/recompute'))}
                >
                  <Icon name="refresh" /> {t('recomputeNow')}
                </button>
              )}
              {/* ★ What counts is a SETTING, so it behaves like one: a button, not twenty spin
                  boxes under the roster somebody opened to read a number. Setting it is part of
                  the paid "Own events" module; without it the button is locked. */}
              {canBuild === false ? (
                <LockedCreateButton label={t('whatCounts')} />
              ) : (
                <button
                  type="button"
                  className="bravos-submit bravos-submit--ghost"
                  onClick={() => navigate('/engagement/rules')}
                >
                  <Icon name="settings" /> {t('whatCounts')}
                </button>
              )}
              {/* Badges people earn on this score — the business unit's set and the company's own. */}
              <button
                type="button"
                className="bravos-submit bravos-submit--ghost"
                onClick={() => navigate('/engagement/badges')}
              >
                <Icon name="award" /> {t('badges')}
              </button>
            </div>
          </>
        )}
        {notice && <p className="bravos-cal__notice">{notice}</p>}
      </Card>

      {/* --- what people say about each other ---------------------------------------------- */}
      {tab === 'cheers' && (
        <Card>
          <Title my="1">{t('cheersStatsTitle')}</Title>
          <CheerStats />
        </Card>
      )}

      {/* --- who they are ----------------------------------------------------------------- */}
      {/* ★ The game's own report ABOVE the wall, not under it: the wall is forty faces tall in
          any company worth reading it for, and a report you have to scroll a directory to reach
          is a report nobody opens. It is short — a line of pills and two lists. */}
      {tab === 'community' && (
        <Card>
          <Title my="1">{t('quizStatsTitle')}</Title>
          <QuizStats />
        </Card>
      )}
      {tab === 'community' && (
        <Card>
          <Title my="1">{t('communityTitle')}</Title>
          <CommunityWall />
        </Card>
      )}

      {/* --- how our mail signs off ------------------------------------------------------- */}
      {tab === 'signoff' && (
        <Card>
          <Title my="1">{t('signOffTitle')}</Title>
          <MailSignOff />
        </Card>
      )}

      {/* --- what we send on their behalf -------------------------------------------------- */}
      {tab === 'messages' && (
        <Card>
          <Title my="1">{t('catalogueTitle')}</Title>
          <MailCatalogue onGoToSignOff={() => setTab('signoff')} />
        </Card>
      )}

      {/* --- the survey they already run, beside ours --------------------------------------- */}
      {tab === 'survey' && (
        <Card>
          <Title my="1">{t('extSurveyTitle')}</Title>
          <ExternalSurvey />
        </Card>
      )}

      {/* --- whether it reaches anybody ---------------------------------------------------- */}
      {tab === 'reach' && (
        <Card>
          <Title my="1">{t('statsReachTitle')}</Title>
          <CommsStats />
        </Card>
      )}

      {/* --- and what came of it ----------------------------------------------------------- */}
      {tab === 'events' && (
        <Card>
          <Title my="1">{t('statsEventsTitle')}</Title>
          <EventStats />
        </Card>
      )}

      {/* --- the roster ------------------------------------------------------------------- */}
      {tab === 'people' && (
      <Card>
        <Title my="1">{t('ourPeople')}</Title>
        {people.length === 0 && <p className="ce-note">{t('noPeopleYet')}</p>}

        {people.map((p) => (
          <div key={p.id} className="eng-row">
            <button
              type="button"
              className="eng-row__main"
              onClick={() => setOpenPerson(openPerson === p.id ? null : p.id)}
            >
              <span className={`eng-score ${band(p.score)}`}>{p.score}</span>
              <Avatar person={p} />
              <span className="ce-person__body">
                <span className="ce-person__name">{p.name || p.email}</span>
                <span className="ce-person__email">{p.email}</span>
                {(p.tags || []).length > 0 && (
                  <span className="bravos-tags eng-row__tags">
                    {p.tags.map((tag) => (
                      <span key={tag} className="bravos-tag">{tag}</span>
                    ))}
                  </span>
                )}
              </span>
              <Icon name={openPerson === p.id ? 'angle-up' : 'angle-down'} />
            </button>

            {/* The arithmetic, in the person's own row: how many times, how many counted (a cap
                is visible only where it bites), and what that was worth. */}
            {person && person.id === p.id && (
              <div className="eng-parts">
                {Object.keys(p.parts || {}).length === 0 ? (
                  <p className="ce-note">{t('nothingYet')}</p>
                ) : Object.entries(p.parts).map(([signal, part]) => (
                  <div key={signal} className="eng-part">
                    <Icon name={FAMILY_ICONS[part.family] || 'star'} />
                    <span className="eng-part__label">
                      {part.tag ? `#${part.tag}` : t(`signal_${signal}`)}
                    </span>
                    <span className="eng-part__n">
                      {part.counted < part.count
                        ? t('countedOfCount', { counted: part.counted, count: part.count })
                        : part.count}
                    </span>
                    <span className="eng-part__pts">+{part.points}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </Card>
      )}

    </Base>
  );
};

export default CompanyEngagement;
